<template>
  <v-row>
    <v-col cols="12">
      <v-card class="cust-card" flat>
        <!-- HEADER BAND -->
        <div class="cust-header pl-base-bg">
          <div class="cust-header-icon">
            <v-icon color="#fff" size="22">mdi-account-edit</v-icon>
          </div>
          <div>
            <div class="cust-title">Edit Customer</div>
            <div class="cust-subtitle">{{ form.name || "Update customer details" }}</div>
          </div>
        </div>

        <!-- FORM -->
        <v-form ref="form" v-model="valid" class="pa-6">
          <!-- CONTACT DETAILS -->
          <div class="cust-section-label">Contact Details</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.name"
                label="Customer Name"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-account"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.email"
                label="Email"
                outlined
                dense
                :rules="[rules.email]"
                prepend-inner-icon="mdi-email"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.phone"
                label="Phone"
                outlined
                dense
                prepend-inner-icon="mdi-phone"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.gstin"
                label="GSTIN"
                outlined
                dense
                prepend-inner-icon="mdi-file-document"
              />
            </v-col>
          </v-row>

          <!-- ADDRESS -->
          <div class="cust-section-label mt-2">Address</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.pincode"
                label="Pincode"
                outlined
                dense
                :rules="[rules.pincode]"
                :loading="pincodeLoading"
                maxlength="6"
                prepend-inner-icon="mdi-map-marker-radius"
                @input="onPincodeInput"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.city"
                label="City"
                outlined
                dense
                prepend-inner-icon="mdi-city"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.state"
                label="State"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.country"
                label="Country"
                outlined
                dense
                prepend-inner-icon="mdi-earth"
              />
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="form.address"
                label="Address"
                outlined
                dense
                rows="2"
                prepend-inner-icon="mdi-home"
              />
            </v-col>
          </v-row>

          <!-- ACTIONS -->
          <v-row class="mt-4" justify="end" dense>
            <v-col v-if="!isViewOnly" cols="4" sm="auto">
              <v-btn
                block
                color="error"
                outlined
                @click="confirmDelete = true"
              >
                Delete
              </v-btn>
            </v-col>

            <v-col cols="4" sm="auto">
              <v-btn block color="secondary" @click="fetchCustomer">Reset</v-btn>
            </v-col>

            <v-col v-if="!isViewOnly" cols="4" sm="auto">
              <v-btn
                block
                class="cust-save-btn"
                :loading="loading"
                @click="submit"
              >
                Update
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-col>

    <global-dialog
      v-if="confirmDelete"
      v-model="confirmDelete"
      :buttons="buttons"
      title="Confirm Delete"
    >
      <p>Are you sure you want to delete this customer?</p>
    </global-dialog>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import GlobalDialog from "./common/global-dialog.vue";
import { lookupPincode, PINCODE_RULE } from "@/utils/pincode";

export default {
  name: "EditCustomer",
  components: { GlobalDialog },

  data() {
    return {
      valid: false,
      loading: false,
      confirmDelete: false,
      pincodeLoading: false,
      pincodeDebounce: null,

      form: {
        name: "",
        email: "",
        phone: "",
        gstin: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        country: "",
      },

      rules: {
        required: (v) => !!v || "Required",
        email: (v) => !v || /.+@.+\..+/.test(v) || "Invalid email",
        pincode: PINCODE_RULE,
      },
    };
  },

  created() {
    this.fetchCustomer();
  },

  computed: {
    buttons() {
      return [
        {
          text: "Cancel",
          action: () => (this.confirmDelete = false),
          outlined: true,
          color: "#0a5a90",
        },
        { text: "Delete", action: this.delete, color: "error" },
      ];
    },
  },

  methods: {
    ...mapActions("customers", ["getCustomerById", "updateCustomer"]),

    async fetchCustomer() {
      try {
        const id = this.$route.params.id;

        const res = await this.getCustomerById(id);

        const c = res.data || res;

        this.form = {
          name: c.name || "",
          email: c.email || "",
          phone: c.phone || "",
          gstin: c.gstin || "",
          address: c.address || "",
          city: c.city || "",
          state: c.state || "",
          pincode: c.pincode || "",
          country: c.country || "",
        };
      } catch (err) {
        this.$toast.error("Failed to load customer");
      }
    },

    onPincodeInput(value) {
      clearTimeout(this.pincodeDebounce);
      if (!/^\d{6}$/.test(value)) return;

      this.pincodeDebounce = setTimeout(async () => {
        this.pincodeLoading = true;
        const result = await lookupPincode(value);
        this.pincodeLoading = false;

        if (!result) return;
        this.form.city = result.city || this.form.city;
        this.form.state = result.state || this.form.state;
        this.form.country = result.country || this.form.country;
      }, 400);
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        const id = this.$route.params.id;

        // normalize
        this.form.gstin = this.form.gstin?.toUpperCase();

        await this.updateCustomer({
          customerId: id,
          customerData: this.form,
        });

        this.$toast.success("Customer updated successfully");

        this.$router.push("/customers");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Failed to update customer",
        );
      } finally {
        this.loading = false;
      }
    },

    async delete() {
      const id = this.$route.params.id;

      try {
        await this.$store.dispatch("customers/deleteCustomer", id);

        this.$toast?.success("Customer deleted successfully");
        this.$router.push("/customers");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to delete customer",
        );
      } finally {
        this.confirmDelete = false;
      }
    },
  },
};
</script>

<style scoped>
.cust-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.cust-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.cust-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cust-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.cust-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.cust-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.cust-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
</style>
